import { useMemo, useState } from 'react';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import useIntl from '../../hooks/useIntl';
import useModalProps from '../../hooks/useModalProps';
import { useAppFlags } from '../../utils/featureFlags';
import { useAsyncPropertyDetails } from '../ViewProperty/hooks';
import { useFetchWaitlistConfiguration } from '../../hooks/data-fetching/useFetchWaitlistV2';
import { getPreferenceOptions } from '../ConvertToApplicantModal/utils/preferences';

import useFetchBedroomSizes from './hooks.useBedroomSizes';
import useAffordableWaitlistPositionsQuery from './hooks.useAffordableWaitlistPositionsQuery';
import useAffordableWaitlistCSVorPDFExport from '../ManageAffordableWaitlist/hooks.useAffordableWaitlistCSVorPDFExport';
// eslint-disable-next-line max-len
import useManageAffordableWaitlistPropertyPreferences from '../ManageAffordableWaitlist/hooks.useManageAffordableWaitlistPropertyPreferences';
import useRows from './hooks.useRows';
import useHeaders from './hooks.useHeaders';
import useFilterOptions from './hooks.useFilterOptions';
import useTexting from './hooks.useTexting';
import {
  AffordableWaitlistPositionStatuses,
  PROPERTY_PATH_MAP,
} from './constants';

const useManageAffordableWaitlistV2 = ({ organizationId, propertyId }) => {
  const name = useUniqueTableName('manage-affordable-waitlist-v2-table');
  const intl = useIntl();
  const flags = useAppFlags();

  const [propertyDetails] = useAsyncPropertyDetails(
    organizationId,
    propertyId,
    intl,
  );

  const separateByBedroom = Boolean(
    propertyDetails?.setup?.waitlistV2?.separateByBedroom,
  );
  const usePreferencesForRanking = Boolean(
    propertyDetails?.setup?.waitlistV2?.usePreferencesForRanking,
  );

  const waitlistV2Programs = propertyDetails?.setup?.waitlistV2?.programs;

  const affordableWaitlistEnabled = Boolean(
    propertyDetails?.setup?.affordableWaitlist,
  );

  const { bedroomCounts, floorPlans, isLoadingBedroomSizes } =
    useFetchBedroomSizes({
      organizationId,
      propertyId,
    });

  // useAsyncPropertyDetails's initial state has neither id nor setup, so
  // separateByBedroom reads false on the mount render regardless of the
  // property's real setting. id only appears once the load resolves.
  const isPropertySetupLoaded = Boolean(propertyDetails?.id);

  const [selectedBedroomCount, setSelectedBedroomCount] = useState(undefined);

  // The agent's pick when there is one, otherwise the property's lowest bedroom
  // size. Derived rather than seeded, so no extra render and no request goes out
  // before the mix is known.
  const activeBedroomCount = separateByBedroom
    ? selectedBedroomCount ?? bedroomCounts[0]
    : undefined;

  const [isIncludeAll, setIsIncludeAll] = useState(false);

  const {
    positions,
    isLoading: isLoadingPositions,
    refetch: refetchPositions,
  } = useAffordableWaitlistPositionsQuery({
    organizationId,
    propertyId,
    bedroomCount: activeBedroomCount,
    status: isIncludeAll
      ? undefined
      : AffordableWaitlistPositionStatuses.WAITING,
    // Gated on property setup having resolved first: until then separateByBedroom
    // reads false for every property, ON or OFF, and an unfiltered request would
    // go out before the app knows which it is. The second half is keyed on the
    // loading flag, not on activeBedroomCount being defined: an ON property whose
    // floor plans load empty has no bedroom to filter by and must still fetch,
    // unfiltered.
    enabled:
      isPropertySetupLoaded && !(separateByBedroom && isLoadingBedroomSizes),
  });

  const {
    data: waitlistConfiguration,
    isLoading: isLoadingWaitlistConfiguration,
  } = useFetchWaitlistConfiguration(propertyId ?? '');

  const preferenceOptions = useMemo(
    () => getPreferenceOptions(waitlistConfiguration?.preferences),
    [waitlistConfiguration?.preferences],
  );

  const positionsWithTexting = useTexting({
    positions,
    leslieEnabled: Boolean(flags.leslie),
  });

  const dataFilterOptions = useFilterOptions({ positions });

  const headers = useHeaders({
    usePreferencesForRanking,
    preferenceOptions,
    dataFilterOptions,
    flags,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'position',
    initialOrder: 'ASC',
  });

  const sortedAndFilteredPositions = useTableFilterSortData({
    results: positionsWithTexting,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const selectFromWaitlistModalProps = useModalProps();
  const denyModalProps = useModalProps();
  const cancelModalProps = useModalProps();
  const reinstateModalProps = useModalProps();

  const rows = useRows({
    positions: sortedAndFilteredPositions,
    propertyId,
    openSelectFromWaitlist: selectFromWaitlistModalProps.handleOpen,
    openDeny: denyModalProps.handleOpen,
    openCancel: cancelModalProps.handleOpen,
    openReinstate: reinstateModalProps.handleOpen,
    preferenceOptions,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const { onCSVButtonClick, onPDFButtonClick } =
    useAffordableWaitlistCSVorPDFExport({
      filteredHeaders,
      rows,
      hasAnyFilters: Boolean(Object.keys(filterState)?.length),
    });

  const configurePreferencesModalProps = useModalProps();

  const {
    data: affordableWaitlistPropertyPreferencesData,
    refetch: refetchAffordableWaitlistPropertyPreferences,
  } = useManageAffordableWaitlistPropertyPreferences({
    organizationId,
    propertyId,
  });

  const configurePreferencesDefaultValues = {
    id: affordableWaitlistPropertyPreferencesData?.id,
    options: (
      affordableWaitlistPropertyPreferencesData?.preferences ?? []
    ).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
  };

  return {
    name,
    isLoading:
      isLoadingBedroomSizes ||
      isLoadingPositions ||
      isLoadingWaitlistConfiguration,
    count: rows.length,
    totalCount: positions.length,
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    filteredHeaders,
    rows,
    bedroomCounts,
    floorPlans,
    selectedBedroomCount: activeBedroomCount,
    setSelectedBedroomCount,
    separateByBedroom,
    usePreferencesForRanking,
    waitlistV2Programs,
    refetchPositions,
    selectFromWaitlistModalProps,
    denyModalProps,
    cancelModalProps,
    reinstateModalProps,
    isIncludeAll,
    setIsIncludeAll,
    onCSVButtonClick,
    onPDFButtonClick,
    affordableWaitlistEnabled,
    configurePreferencesModalProps,
    configurePreferencesDefaultValues,
    refetchAffordableWaitlistPropertyPreferences,
  };
};

export default useManageAffordableWaitlistV2;
